export const rawPhoneNumber = phone => phone.replace(/[()\s]/g, "");

/**
 * Normalizes phone number to digits only (removes all non-digit characters)
 * Useful for comparing phone numbers regardless of formatting
 *
 * @param {string} phone - Phone number (formatted or raw)
 * @returns {string} Phone number with only digits
 *
 * @example
 * normalizePhoneNumber("(555) 123-4567")  // Returns "5551234567"
 * normalizePhoneNumber("555-123-4567")    // Returns "5551234567"
 * normalizePhoneNumber("5551234567")      // Returns "5551234567"
 */
export const normalizePhoneNumber = phone => {
  if (!phone) return "";
  return phone.replace(/\D/g, "");
};

/**
 * Formats a phone number string into US phone number format.
 * Strips all non-numeric characters and formats the result as (XXX) XXX-XXXX.
 * Handles US/Canada country code (1) by stripping it if present.
 *
 * @param {string} input - The phone number string to format (can contain any characters)
 * @returns {string} The formatted phone number string
 *
 * @example
 * formatPhoneNumber("1234567890")        // Returns "(123) 456-7890"
 * formatPhoneNumber("123-456-7890")      // Returns "(123) 456-7890"
 * formatPhoneNumber("+17072294267")      // Returns "(707) 229-4267"
 * formatPhoneNumber("17072294267")       // Returns "(707) 229-4267"
 * formatPhoneNumber("123")               // Returns "123"
 */
export const formatPhoneNumber = input => {
  let cleanedInput = input.replace(/\D/g, "");

  // Strip leading '1' if we have 11 digits (US/Canada country code)
  if (cleanedInput.length === 11 && cleanedInput[0] === "1") {
    cleanedInput = cleanedInput.slice(1);
  }

  let formatted = "";

  if (cleanedInput.length <= 3) {
    formatted = cleanedInput;
  } else if (cleanedInput.length <= 6) {
    formatted = `(${cleanedInput.slice(0, 3)}) ${cleanedInput.slice(3)}`;
  } else {
    formatted = `(${cleanedInput.slice(0, 3)}) ${cleanedInput.slice(3, 6)}-${cleanedInput.slice(6, 10)}`;
  }

  return formatted;
};

const restorePreviousValue = (target, previousValue) => {
  const prevCursor = parseInt(target.dataset.cursorPosition || previousValue.length, 10);
  target.value = previousValue;
  target.setSelectionRange(prevCursor, prevCursor);
};

const salvageDigits = (target, allDigits) => {
  const salvaged = allDigits[0] === "1" ? allDigits.slice(0, 11) : allDigits.slice(0, 10);
  const formatted = formatPhoneNumber(salvaged);
  target.value = formatted;
  target.setSelectionRange(formatted.length, formatted.length);
  target.dataset.formattedValue = formatted;
  target.dataset.cursorPosition = formatted.length.toString();
};

const calculateNewCursorPosition = (formatted, adjustedOldDigits) => {
  // If cursor was at the very beginning (0 digits before it), keep it there
  if (adjustedOldDigits === 0) {
    return 0;
  }

  let newCursorPosition = 0;
  let digitCount = 0;
  for (let i = 0; i < formatted.length; i += 1) {
    if (formatted[i].match(/\d/)) {
      digitCount += 1;
      if (digitCount === adjustedOldDigits) {
        newCursorPosition = i + 1;
        break;
      }
    }
  }

  // If we didn't find enough digits, place at end
  if (digitCount < adjustedOldDigits) {
    newCursorPosition = formatted.length;
  }

  return newCursorPosition;
};

/**
 * Formats a phone number input while preserving cursor position.
 * This function should be used in input event handlers to format phone numbers
 * without causing the cursor to jump to the end.
 * Prevents entering more than 10 digits (or 11 with leading '1') to avoid truncation.
 *
 * @param {Event} event - The input event from the phone input field
 *
 * @example
 * phoneInput.addEventListener("input", formatPhoneNumberWithCursor);
 */
export const formatPhoneNumberWithCursor = event => {
  const cursorPosition = event.target.selectionStart;
  const newValue = event.target.value;
  const allDigits = newValue.replace(/\D/g, "");
  const isValidLength = allDigits.length <= 10 || (allDigits.length === 11 && allDigits[0] === "1");
  const previousValue = event.target.dataset.formattedValue || "";

  if (!isValidLength) {
    if (previousValue) {
      restorePreviousValue(event.target, previousValue);
    } else if (allDigits.length >= 11) {
      salvageDigits(event.target, allDigits);
    }
    return;
  }

  const oldDigitsBeforeCursor = newValue.slice(0, cursorPosition).replace(/\D/g, "").length;
  const formatted = formatPhoneNumber(newValue);
  event.target.value = formatted;

  const strippedLeading1 = allDigits.length === 11 && allDigits[0] === "1";
  const adjustedOldDigits =
    strippedLeading1 && oldDigitsBeforeCursor > 0
      ? oldDigitsBeforeCursor - 1
      : oldDigitsBeforeCursor;

  const newCursorPosition = calculateNewCursorPosition(formatted, adjustedOldDigits);
  event.target.setSelectionRange(newCursorPosition, newCursorPosition);
  event.target.dataset.formattedValue = formatted;
  event.target.dataset.cursorPosition = newCursorPosition.toString();
};
